import { useEffect, useRef, useState } from 'react';

// Tracks leaving the test window and blocks pasting anything copied outside the test.
export default function useIntegrity({ active, onEvent, initial }) {
  const [counts, setCounts] = useState(() => {
    const base = { tab_switches: 0, paste_blocked: 0, away_seconds: 0 };
    Object.keys(base).forEach((k) => { base[k] = Number(initial?.[k]) || 0; });
    return base;
  });
  const ref = useRef({ lastCopy: 0, lastBlur: 0, awayStart: 0 });
  const cb = useRef(onEvent);
  cb.current = onEvent;

  useEffect(() => {
    if (!active) return undefined;
    const st = ref.current;
    const bump = (key, by = 1) => setCounts((c) => ({ ...c, [key]: c[key] + by }));
    const leave = () => {
      if (st.awayStart) return;
      st.awayStart = Date.now();
      st.lastBlur = Date.now();
    };
    const back = () => {
      if (!st.awayStart) return;
      const secs = Math.round((Date.now() - st.awayStart) / 1000);
      st.awayStart = 0;
      if (secs < 1) return;
      bump('tab_switches');
      bump('away_seconds', secs);
      cb.current?.('tab_switch', secs);
    };
    const onVis = () => (document.hidden ? leave() : back());
    const markCopy = () => { st.lastCopy = Date.now(); };
    const internal = () => st.lastCopy > 0 && st.lastCopy > st.lastBlur;
    const block = (e) => {
      e.preventDefault();
      e.stopImmediatePropagation();
      bump('paste_blocked');
      cb.current?.('paste_blocked');
    };
    const onKey = (e) => {
      const mod = e.ctrlKey || e.metaKey;
      if (!mod) return;
      const k = e.key.toLowerCase();
      if (k === 'c' || k === 'x') markCopy();
      if (k === 'v' && !internal()) block(e);
    };
    const onPaste = (e) => { if (!internal()) block(e); };
    const onDrop = (e) => { if (e.dataTransfer?.types?.length) { e.preventDefault(); e.stopImmediatePropagation(); } };
    const onCtx = (e) => { if (!e.target.closest?.('[data-testid="univer-spreadsheet-container"]')) e.preventDefault(); };

    window.addEventListener('blur', leave);
    window.addEventListener('focus', back);
    document.addEventListener('visibilitychange', onVis);
    window.addEventListener('keydown', onKey, true);
    window.addEventListener('copy', markCopy, true);
    window.addEventListener('cut', markCopy, true);
    window.addEventListener('paste', onPaste, true);
    window.addEventListener('drop', onDrop, true);
    window.addEventListener('contextmenu', onCtx, true);
    return () => {
      window.removeEventListener('blur', leave);
      window.removeEventListener('focus', back);
      document.removeEventListener('visibilitychange', onVis);
      window.removeEventListener('keydown', onKey, true);
      window.removeEventListener('copy', markCopy, true);
      window.removeEventListener('cut', markCopy, true);
      window.removeEventListener('paste', onPaste, true);
      window.removeEventListener('drop', onDrop, true);
      window.removeEventListener('contextmenu', onCtx, true);
    };
  }, [active]);

  return [counts, setCounts];
}
